import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift } from "lucide-react";
import { useLang } from "./LangContext";

export default function PromoPopup() {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !dismissed) {
          setShow(true);
        }
      },
      { threshold: 0.3 }
    );

    const section = document.querySelector("#tarifs");
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, [dismissed]);

  const dismiss = () => {
    setShow(false);
    setDismissed(true);
  };

  const claim = () => {
    dismiss();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="fixed bottom-24 right-6 z-[80] max-w-sm"
          data-testid="promo-popup"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#73B5E2] to-[#5AA0D0]" />

            <button
              onClick={dismiss}
              data-testid="promo-popup-close"
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>

            <div className="flex items-start gap-4">
              <motion.div
                animate={{ rotate: [0, -10, 10, -5, 0] }}
                transition={{ duration: 1, delay: 0.5 }}
                className="w-12 h-12 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center flex-shrink-0"
              >
                <Gift className="w-6 h-6 text-[#73B5E2]" />
              </motion.div>
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                  {t.promo.title}
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">
                  {t.promo.text}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={claim}
                    data-testid="promo-popup-cta"
                    className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-5 py-2 text-sm font-medium transition-all hover:scale-105 shadow-md shadow-blue-500/20"
                  >
                    {t.promo.cta}
                  </button>
                  <button
                    onClick={dismiss}
                    data-testid="promo-popup-dismiss"
                    className="text-slate-400 hover:text-slate-600 text-sm font-medium transition-colors"
                  >
                    {t.promo.close}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
